import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'phosphor-react';
import { cn } from '../utils/cn';
import { Button } from './Button';

interface BottomSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
}

interface BottomSheetContentProps {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  onEscapeKeyDown?: (e: KeyboardEvent) => void;
  onPointerDownOutside?: (e: PointerEvent) => void;
}

interface BottomSheetHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

interface BottomSheetTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
  children: React.ReactNode;
}

interface BottomSheetDescriptionProps extends React.HTMLAttributes<HTMLParagraphElement> {
  children: React.ReactNode;
}

interface BottomSheetFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

const BottomSheet: React.FC<BottomSheetProps> = ({ open, onOpenChange, children }) => {
  // Lock body scroll when bottom sheet is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-end justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => onOpenChange(false)}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/40" />
          
          {/* Bottom Sheet Container */}
          <div className="relative z-10 w-full">
            {React.Children.map(children, (child) => {
              if (React.isValidElement<BottomSheetContentProps>(child)) {
                const childProps = child.props as BottomSheetContentProps;
                return React.cloneElement(child, {
                  ...childProps,
                  onEscapeKeyDown: (e: KeyboardEvent) => {
                    if (e.key === 'Escape') {
                      e.stopPropagation();
                      onOpenChange(false);
                    }
                    childProps.onEscapeKeyDown?.(e);
                  },
                  onPointerDownOutside: (e: PointerEvent) => {
                    e.stopPropagation();
                    onOpenChange(false);
                    childProps.onPointerDownOutside?.(e);
                  },
                });
              }
              return child;
            })}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const BottomSheetContent: React.FC<BottomSheetContentProps> = ({
  children,
  className,
  onEscapeKeyDown,
  onPointerDownOutside,
  style,
}) => {
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onEscapeKeyDown?.(e);
      }
    };

    document.addEventListener('keydown', handleEscape, true);
    return () => document.removeEventListener('keydown', handleEscape, true);
  }, [onEscapeKeyDown]);

  return (
    <motion.div
      initial={{ y: '100%' }}
      animate={{ y: 0 }}
      exit={{ y: '100%' }}
      transition={{ type: 'spring', damping: 30, stiffness: 300 }}
      className={cn(
        "bg-background rounded-t-3xl w-full relative max-h-[90vh] flex flex-col overflow-hidden",
        className
      )}
      style={style}
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => {
        if (e.target === e.currentTarget) {
          onPointerDownOutside?.(e.nativeEvent as PointerEvent);
        }
      }}
    >
      {/* Handle Bar */}
      <div className="flex items-center justify-center pt-3 pb-2">
        <div className="bg-[rgba(0,0,0,0.12)] rounded-full w-10 h-1" />
      </div>
      
      {children}
    </motion.div>
  );
};

const BottomSheetHeader: React.FC<BottomSheetHeaderProps> = ({
  children,
  className,
  ...props
}) => {
  return (
    <div
      className={cn("flex flex-col space-y-1.5 px-4 pb-4", className)}
      {...props}
    >
      {children}
    </div>
  );
};

const BottomSheetTitle: React.FC<BottomSheetTitleProps> = ({
  children,
  className,
  ...props
}) => {
  return (
    <h3
      className={cn(
        "font-sans font-semibold text-lg text-foreground tracking-[-0.18px]",
        className
      )}
      {...props}
    >
      {children}
    </h3>
  );
};

const BottomSheetDescription: React.FC<BottomSheetDescriptionProps> = ({
  children,
  className,
  ...props
}) => {
  return (
    <p
      className={cn(
        "font-sans font-normal text-sm text-text-subtle",
        className
      )}
      {...props}
    >
      {children}
    </p>
  );
};

const BottomSheetFooter: React.FC<BottomSheetFooterProps> = ({
  children,
  className,
  ...props
}) => {
  return (
    <div
      className={cn("flex flex-col gap-3 px-4 pb-4 pt-4 border-t border-border", className)}
      {...props}
    >
      {children}
    </div>
  );
};

interface BottomSheetCloseProps {
  onClose: () => void;
  className?: string;
}

const BottomSheetClose: React.FC<BottomSheetCloseProps> = ({ onClose, className }) => {
  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={onClose}
      className={cn(
        "absolute right-4 top-4 z-10",
        className
      )}
      aria-label="Cerrar"
    >
      <X size={20} weight="bold" />
    </Button>
  );
};

export {
  BottomSheet,
  BottomSheetContent,
  BottomSheetHeader,
  BottomSheetTitle,
  BottomSheetDescription,
  BottomSheetFooter,
  BottomSheetClose,
};

